/**
 * Truth Data Hook
 * 
 * Loads and filters TRUTH_SUMMARY.json for use in Help Center and other components.
 * Provides role-scoped and route-scoped truth data.
 * 
 * Key Features:
 * - Lazy loading of truth data
 * - Role-based filtering
 * - Route-aware context
 * - Type-safe access to routes and capabilities
 */

import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';

export interface TruthRoute {
  path: string;
  userType: 'PUBLIC' | 'SRL' | 'PRO' | 'ADMIN' | 'INTERNAL_DEBUG';
  component: string;
  guards: string[];
  caseScoped: boolean;
  navReachability: 'NAV_PRIMARY' | 'NAV_FLOW' | 'NAV_CONTEXTUAL' | 'NAV_DIRECT' | 'NAV_INTERNAL';
  uiEntryPoints: string[];
  notes: string[];
}

export interface TruthCapability {
  key: string;
  tag: string;
  exposure: 'CORE' | 'SUPPORTING' | 'ADVANCED';
  claim: string;
  userTypes: string[];
  routes: string[];
  disclaimers?: string[];
}

export interface TruthBundle {
  generatedAt: string;
  sources: string[];
  routes: TruthRoute[];
  capabilities: TruthCapability[];
}

export type UserRole = 'srl' | 'pro' | 'admin';

/**
 * Get user role from auth claims and route context
 */
function resolveUserRole(claims: any, pathname: string): UserRole {
  // Route context takes precedence for dual-role users
  if (pathname.startsWith('/admin')) {
    return 'admin';
  }
  if (pathname.startsWith('/pro')) {
    return 'pro';
  }
  
  // Fallback to auth claims
  if (claims?.role === 'super' || claims?.role === 'support') {
    return 'admin';
  }
  if (claims?.isPro === true) {
    return 'pro';
  }
  return 'srl';
}

/**
 * Map role to user type for filtering
 */
function roleToUserType(role: UserRole): TruthRoute['userType'] {
  const map: Record<UserRole, TruthRoute['userType']> = {
    'srl': 'SRL',
    'pro': 'PRO',
    'admin': 'ADMIN',
  };
  return map[role];
}

/**
 * Hook to load and filter truth data
 */
export function useTruthData() {
  const { claims } = useAuth();
  const location = useLocation();
  const [truthData, setTruthData] = useState<TruthBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Determine current role
  const activeRole = useMemo(
    () => resolveUserRole(claims, location.pathname),
    [claims, location.pathname]
  );

  // Load truth data on mount
  useEffect(() => {
    async function loadTruthData() {
      try {
        const response = await fetch('/docs/truth/TRUTH_SUMMARY.json');
        if (!response.ok) {
          throw new Error(`Failed to load truth data: ${response.statusText}`);
        }
        const data: TruthBundle = await response.json();
        setTruthData(data);
        setError(null);
      } catch (err) {
        console.error('[useTruthData] Load error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load truth data');
      } finally {
        setLoading(false);
      }
    }

    loadTruthData();
  }, []);

  // Filter routes by role
  const roleRoutes = useMemo(() => {
    if (!truthData) return [];
    const userType = roleToUserType(activeRole);
    return truthData.routes.filter(r => r.userType === userType);
  }, [truthData, activeRole]);

  // Filter capabilities by role
  const roleCapabilities = useMemo(() => {
    if (!truthData) return [];
    const userType = roleToUserType(activeRole);
    return truthData.capabilities.filter(cap => 
      cap.userTypes.includes(userType)
    );
  }, [truthData, activeRole]);

  // Find current route info
  const currentRoute = useMemo(() => {
    if (!truthData) return null;
    
    // Exact match first
    const exact = truthData.routes.find(r => r.path === location.pathname);
    if (exact) return exact;
    
    // Pattern match for dynamic routes (e.g., /cases/:caseId)
    const pattern = truthData.routes.find(r => {
      if (!r.path.includes(':')) return false;
      
      const regex = new RegExp(
        '^' + r.path.replace(/:[^/]+/g, '[^/]+') + '$'
      );
      return regex.test(location.pathname);
    });
    
    return pattern || null;
  }, [truthData, location.pathname]);

  // Get CORE capabilities (user-facing features)
  const coreCapabilities = useMemo(() => {
    return roleCapabilities.filter(cap => cap.exposure === 'CORE');
  }, [roleCapabilities]);

  // Get capabilities for current route
  const routeCapabilities = useMemo(() => {
    if (!currentRoute || !truthData) return [];
    
    return truthData.capabilities.filter(cap =>
      cap.routes.some(routePath => {
        // Check if current route matches capability route
        if (routePath === currentRoute.path) return true;
        
        // Check if current route is within capability's route scope
        if (routePath.includes(':') && currentRoute.path.includes(':')) {
          const baseRoute = routePath.split(':')[0];
          const currentBase = currentRoute.path.split(':')[0];
          return currentBase.startsWith(baseRoute);
        }
        
        return false;
      })
    );
  }, [currentRoute, truthData]);

  return {
    loading,
    error,
    truthData,
    activeRole,
    roleRoutes,
    roleCapabilities,
    coreCapabilities,
    currentRoute,
    routeCapabilities,
  };
}
